import { getAppSessionId } from '@makemydeal/dr-shared-ui-utils/sessionUtils/sessionUtils';
import { getGlobalFeatureToggle } from '@makemydeal/dr-shared-ui-utils/documentUtils/documentUtils';
import { getOktaTokenAsync } from './common';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { decodeGzipBuffer } from './gzipDecoder';

type CallbackWithArgs = (args: unknown) => void;

const SUBSCRIBE = 'subscribe';
const CMD = 'CMD';

type Toggles = {
    enableOptionalPbcUi?: boolean;
    enableGenerateContracts?: boolean;
    isDisplayContractStatusEnabled?: boolean;
};

type Selectors = {
    getDealRefId?: () => string;
};

type ReinitializeConfig = {
    connectionFailureLogThreshold?: number;
    healthCheckInterval?: number;
};

type SocketManagerV2Props = {
    messageCallback: CallbackWithArgs;
    dealExchangeId: string;
    selectors: Selectors;
    onConnectionFailure?: (failed: boolean) => void;
};

export default class SocketManagerV2 {
    static KEEP_ALIVE_TIMEOUT = 5 * 60 * 1000;
    static AWS_LIMIT_BUFFER_TIMEOUT = 90 * 60 * 1000;
    static HEALTH_CHECK_INTERVAL = 60;
    static MAX_RETRY_ATTEMPTS = 5;
    static RETRY_DELAY_MS = 500;
    static CONNECTION_FAILURE_LOG_THRESHOLD = 10;
    // 2x the 128KB API Gateway wire cap — defensive bound on binary frame decode.
    static MAX_BINARY_FRAME_BYTES = 256 * 1024;

    socket: WebSocket | null;
    healthCheckTimer: any | null;
    connectionString: string | null;
    dealExchangeId: string;
    messageCallback: CallbackWithArgs;
    sessionId: string;

    enableOptionalPbcUi: boolean;
    enableGenerateContracts: boolean;
    isDisplayContractStatusEnabled: boolean;

    retryCount: number;
    onConnectionFailure?: (failed: boolean) => void;
    connectionOpenedAt: number | null;
    lastKeepAliveAt: number | null;
    totalRecoveryIterations: number;
    hasLoggedFailure: boolean;
    selectors: Selectors;

    private _connectionFailureLogThreshold: number | undefined;
    private _healthCheckInterval: number | undefined;
    private _isInitialized: boolean;
    private _messageQueue: Array<unknown | Promise<unknown>>;
    private _isConnecting: boolean;
    private _startGen: number;

    get connectionFailureLogThreshold(): number {
        return this._connectionFailureLogThreshold ?? SocketManagerV2.CONNECTION_FAILURE_LOG_THRESHOLD;
    }

    get healthCheckInterval(): number {
        return this._healthCheckInterval ?? SocketManagerV2.HEALTH_CHECK_INTERVAL;
    }

    constructor(props: SocketManagerV2Props) {
        this.sessionId = getAppSessionId();
        this.socket = null;
        this.healthCheckTimer = null;
        this.connectionString = null;
        this.dealExchangeId = props.dealExchangeId;
        this.selectors = props.selectors || {};
        this.messageCallback = props.messageCallback;
        this.retryCount = 0;
        this.onConnectionFailure = props.onConnectionFailure;

        this.enableOptionalPbcUi = false;
        this.enableGenerateContracts = false;
        this.isDisplayContractStatusEnabled = false;

        this.connectionOpenedAt = null;
        this.lastKeepAliveAt = null;
        this.totalRecoveryIterations = 0;
        this.hasLoggedFailure = false;
        this._isInitialized = false;
        this._messageQueue = [];
        this._isConnecting = false;
        this._startGen = 0;

        this._handleOpen = this._handleOpen.bind(this);
        this._handleMessage = this._handleMessage.bind(this);
        this._handleError = this._handleError.bind(this);
        this._handleClose = this._handleClose.bind(this);
    }

    reinitialize(toggles: Toggles, config: ReinitializeConfig = {}) {
        this.enableOptionalPbcUi = toggles.enableOptionalPbcUi ?? false;
        this.enableGenerateContracts = toggles.enableGenerateContracts ?? false;
        this.isDisplayContractStatusEnabled = toggles.isDisplayContractStatusEnabled ?? false;

        if (config.connectionFailureLogThreshold !== undefined) {
            this._connectionFailureLogThreshold = config.connectionFailureLogThreshold;
        }
        if (config.healthCheckInterval !== undefined) {
            this._healthCheckInterval = config.healthCheckInterval;
        }

        this._isInitialized = true;

        if (this.socket?.readyState === WebSocket.OPEN) {
            this._subscribeToToggleGatedTopics();
        }

        this._flushMessageQueue();
    }

    updateChangedDealExchangeId(dealExchangeId: string) {
        if (!dealExchangeId || dealExchangeId === this.dealExchangeId) {
            return;
        }
        this.dealExchangeId = dealExchangeId;
        if (this.socket?.readyState === WebSocket.OPEN) {
            this._subscribeToTopics();
        }
    }

    private _flushMessageQueue() {
        const queued = this._messageQueue;
        this._messageQueue = [];
        queued.forEach((item) => {
            if (item instanceof Promise) {
                item.then((decoded) => {
                    if (decoded !== undefined) {
                        this.messageCallback(decoded);
                    }
                });
            } else {
                this.messageCallback(item);
            }
        });
    }

    start(shouldTriggerHealthMonitor = false) {
        if (this._isConnecting) return;

        this._isConnecting = true;
        const gen = ++this._startGen;
        this._getConnectionStringAsync().then((connectionString) => {
            if (gen !== this._startGen) return;
            this._isConnecting = false;
            this.connectionString = connectionString;
            if (this.connectionString) {
                this._attachSocket(this.connectionString);
            } else {
                this._logConnectionStatusToNR({
                    meta: { status: 'connectionStringNull' }
                });
            }
            if (shouldTriggerHealthMonitor) {
                this._runHealthMonitor();
            }
        });
    }

    private _detachSocket() {
        if (this.socket) {
            this.socket.removeEventListener('open', this._handleOpen);
            this.socket.removeEventListener('message', this._handleMessage);
            this.socket.removeEventListener('error', this._handleError);
            this.socket.removeEventListener('close', this._handleClose);
            this.socket.close();
            this.socket = null;
        }
    }

    private _attachSocket(connectionString: string) {
        this._detachSocket();
        this.socket = new WebSocket(connectionString);
        this.socket.addEventListener('open', this._handleOpen);
        this.socket.addEventListener('message', this._handleMessage);
        this.socket.addEventListener('error', this._handleError);
        this.socket.addEventListener('close', this._handleClose);
    }

    private _isGzipPayload(buf: ArrayBuffer): boolean {
        if (buf.byteLength < 2) return false;
        const view = new Uint8Array(buf, 0, 2);
        return view[0] === 0x1f && view[1] === 0x8b;
    }

    private async _decodeBinaryFrame(input: ArrayBuffer | string): Promise<unknown> {
        try {
            // A base64 string (the API Gateway transport) is decoded to bytes here so that malformed
            // base64 (e.g. URL-safe chars from a server regression or bad TestCafe input) is caught
            // below and reported as messageDecodeFailure rather than throwing uncaught.
            const buf = typeof input === 'string' ? Uint8Array.from(atob(input), (c) => c.charCodeAt(0)).buffer : input;
            // Defensive cap: reject binary frames larger than 2x the 128KB wire limit
            // to avoid decompression-bomb memory pressure on malformed input.
            if (buf.byteLength > SocketManagerV2.MAX_BINARY_FRAME_BYTES) {
                logNewRelicPageAction('MV:WebSocket', {
                    type: 'messageDecodeFailure',
                    cause: 'binary_frame_exceeds_cap'
                });
                return undefined;
            }
            let text: string;
            if (this._isGzipPayload(buf)) {
                text = await decodeGzipBuffer(buf);
            } else {
                text = new TextDecoder().decode(buf);
            }
            return JSON.parse(text);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            // Report decode failure via PageAction; callers continue — the frame is dropped silently.
            logNewRelicPageAction('MV:WebSocket', {
                type: 'messageDecodeFailure',
                cause: message
            });
            return undefined; // drop silently
        }
    }

    stop() {
        this.retryCount = 0;
        this._startGen++;
        this._isConnecting = false;
        clearTimeout(this.healthCheckTimer as any);
        this._detachSocket();
    }

    destroy() {
        this.stop();
    }

    refresh() {
        this._keepAlive();
    }

    _recreateConnection() {
        this.stop();
        this.start();
    }

    _getWebSocketBaseUrl(): string | undefined {
        if (window.location.hostname.includes('app.coxautoinc.com')) {
            return `/websockets`;
        }
        return process.env.WEBSOCKETS_URL;
    }

    async _getConnectionStringAsync() {
        try {
            const token = (await getOktaTokenAsync()) || 'knownorigin';
            return `${this._getWebSocketBaseUrl()}?token=${token}`;
        } catch (err) {
            return null;
        }
    }

    _send(data: string) {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(data);
        }
    }

    _subscribeToEarlyTopics() {
        this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'dealXgCreate', data: this.dealExchangeId, metadata: CMD }));
        this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'dealXgPatch', data: this.dealExchangeId, metadata: CMD }));
        // Use the shared global-toggle accessor (same source the dash-store socket middleware reads)
        // so the drMultiScenarioLoad subscription and the middleware's V2 setup can't diverge.
        if (getGlobalFeatureToggle('enableMVMSLoadAsync')) {
            this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'drMultiScenarioLoad', data: this.sessionId, metadata: CMD }));
        }
    }

    _subscribeToToggleGatedTopics() {
        this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'pushToFI', data: this.dealExchangeId, metadata: CMD }));
        if (this.enableOptionalPbcUi) {
            this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'mv_pushToDms', data: this.dealExchangeId, metadata: CMD }));
        }
        if ((this.enableGenerateContracts || this.isDisplayContractStatusEnabled) && this.selectors.getDealRefId?.()) {
            this._send(
                JSON.stringify({ action: SUBSCRIBE, topic: 'drContracting', data: this.selectors.getDealRefId!(), metadata: CMD })
            );
        }
        this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'aioDxgEvents', data: this.dealExchangeId, metadata: CMD }));
        this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'mv_docMove', data: this.dealExchangeId, metadata: CMD }));
        // Topic name is the publishing service's own constant (dr-services-deal-pulse
        // `publishBroadcast.ts` BROADCAST_TOPIC), and its filter is the deal exchange id.
        this._send(
            JSON.stringify({
                action: SUBSCRIBE,
                topic: 'dealPulsePurchaseHistory',
                data: this.dealExchangeId,
                metadata: CMD
            })
        );
        this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'dealPulseTradeIn', data: this.dealExchangeId, metadata: CMD }));

        const copyCreditAppDealRefId = this.selectors.getDealRefId?.();
        if (copyCreditAppDealRefId) {
            this._send(JSON.stringify({ action: SUBSCRIBE, topic: 'copyCreditApp', data: copyCreditAppDealRefId, metadata: CMD }));
        }
    }

    _subscribeToTopics() {
        this._subscribeToEarlyTopics();
        if (this._isInitialized) {
            this._subscribeToToggleGatedTopics();
        }
    }

    _keepAlive = () => {
        this.lastKeepAliveAt = Date.now();
        this._subscribeToTopics();
    };

    _runHealthMonitor = () => {
        clearTimeout(this.healthCheckTimer as any);
        this.healthCheckTimer = setTimeout(() => {
            const now = Date.now();

            const isHealthy = this._isConnectionHealthy();
            const shouldRecreate =
                this.connectionOpenedAt && now - this.connectionOpenedAt >= SocketManagerV2.AWS_LIMIT_BUFFER_TIMEOUT;
            const isKeepAliveDue = this.lastKeepAliveAt && now - this.lastKeepAliveAt >= SocketManagerV2.KEEP_ALIVE_TIMEOUT;

            if (!isHealthy) {
                if (this.totalRecoveryIterations >= this.connectionFailureLogThreshold && !this.hasLoggedFailure) {
                    this._logConnectionStatusToNR({
                        meta: {
                            status: 'failed',
                            iteration: this.totalRecoveryIterations
                        }
                    });
                    this.hasLoggedFailure = true;
                    this.onConnectionFailure?.(true);
                }
                this.totalRecoveryIterations++;
                this._recreateConnection();
            } else if (shouldRecreate) {
                this._recreateConnection();
            } else if (isKeepAliveDue) {
                this._keepAlive();
            }

            this._runHealthMonitor();
        }, this.healthCheckInterval * 1000);
    };

    _isConnectionHealthy = () => {
        return !!(this.socket && this.socket.readyState === WebSocket.OPEN);
    };

    _handleOpen(_event: Event) {
        if (this.retryCount > 0 || this.totalRecoveryIterations > 0) {
            this._logConnectionStatusToNR({
                meta: {
                    status: 'recovered',
                    iteration: this.totalRecoveryIterations
                }
            });
            this.retryCount = 0;
            this.onConnectionFailure?.(false);
            this.totalRecoveryIterations = 0;
            this.hasLoggedFailure = false;
        }

        this.connectionOpenedAt = Date.now();
        this.lastKeepAliveAt = Date.now();

        this._subscribeToTopics();
    }

    _handleMessage(event: MessageEvent) {
        let data;
        try {
            data = JSON.parse(event.data);
        } catch (err) {
            data = event.data;
        }

        // A gzipped scenario envelope rides as a base64 string inside the JSON envelope (API Gateway
        // can't deliver raw gzip bytes as a binary frame). Base64-decode to bytes, then run the gzip
        // decode (gzip-magic check → decompress → JSON.parse).
        if (data && typeof data === 'object' && data.preEncoded === true && typeof data.data === 'string') {
            const pending = this._decodeBinaryFrame(data.data);
            this._messageQueue.push(pending);
            if (this._isInitialized) {
                this._flushMessageQueue();
            }
            return;
        }

        if (this._isInitialized) {
            this.messageCallback(data);
        } else {
            this._messageQueue.push(data);
        }
    }

    _handleError(event: Event) {
        // eslint-disable-next-line no-console
        console.warn(event);

        // At short health-check intervals (<= 30s) the periodic health monitor (_runHealthMonitor)
        // already detects an unhealthy socket and recovers it via _recreateConnection on its own cadence.
        // Skipping error-driven retry here avoids two recovery paths reconnecting at once (a reconnect storm);
        // recovery is deferred to the monitor. At the default slower interval (60s) the monitor reacts too
        // slowly, so we fall through to an immediate backoff retry below.
        if (this.healthCheckInterval && this.healthCheckInterval <= 30) {
            return;
        }

        if (this.retryCount >= SocketManagerV2.MAX_RETRY_ATTEMPTS) {
            this._logConnectionStatusToNR({
                meta: {
                    status: 'retryExhausted',
                    iteration: this.totalRecoveryIterations
                }
            });
            this.onConnectionFailure?.(true);
            return;
        }

        this.retryCount++;

        /* istanbul ignore next */
        setTimeout(() => {
            this.start(false);
        }, SocketManagerV2.RETRY_DELAY_MS * this.retryCount);
    }

    _handleClose(_event: CloseEvent) {
        this._logConnectionStatusToNR({
            meta: {
                status: 'closed'
            }
        });
    }

    _logConnectionStatusToNR({ meta = {} }: { meta?: Record<string, string | number | boolean> } = {}) {
        const action = {
            dealId: this.dealExchangeId,
            type: 'connectionOutcome',
            retryCount: this.retryCount,
            ...meta
        };
        logNewRelicPageAction('MV:WebSocket', action);
    }
}
